import api from './apiService';

/**
 * Service for Polar.sh related operations
 */
const polarService = {
  /**
   * Create a new Polar.sh checkout session
   * 
   * @param {Object} options - Checkout options
   * @param {string} options.success_url - URL to redirect after successful checkout
   * @param {string} options.cancel_url - URL to redirect if checkout is cancelled
   * @param {string} [options.mode='subscription'] - Mode of checkout (subscription or one_time)
   * @param {string} [options.subscription_tier_id] - Subscription tier ID (required for subscription mode)
   * @param {string} [options.price_id] - Price ID (required for one_time mode)
   * @param {number} [options.quantity=1] - Quantity of the product
   * @param {object} [options.metadata] - Additional metadata for the checkout session
   * @returns {Promise<Object>} The created checkout session
   */
  createCheckoutSession: async (options) => {
    try {
      const response = await api.post('/polar/checkout', options);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create checkout session');
    }
  },

  /**
   * Get a Polar.sh checkout session by ID
   * 
   * @param {string} id - Checkout session ID
   * @returns {Promise<Object>} The checkout session
   */
  getCheckoutSession: async (id) => {
    try {
      const response = await api.get(`/polar/checkout/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get checkout session');
    }
  },

  /**
   * List Polar.sh checkout sessions
   * 
   * @param {Object} options - Query options
   * @param {number} [options.limit=20] - Number of sessions to return
   * @param {number} [options.offset=0] - Offset for pagination
   * @returns {Promise<Object>} Checkout sessions with pagination info
   */
  listCheckoutSessions: async ({ limit = 20, offset = 0 } = {}) => {
    try {
      const response = await api.get('/polar/checkout', { params: { limit, offset } });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to list checkout sessions');
    }
  },

  /**
   * Verify a customer session token from Polar.sh
   * First syncs with Polar.sh to ensure latest data, then verifies the token
   * 
   * @param {string} token - The customer session token to verify
   * @returns {Promise<Object>} The verified session data including transaction details
   */
  verifyCustomerSessionToken: async (token) => {
    try {
      // First sync data from Polar.sh
      await api.post('/orders/sync-from-polar');
      
      // Then get the checkout session
      const response = await api.get(`/polar/checkout/${token}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to verify customer session token');
    }
  },
  
  /**
   * Track a completed checkout event
   * 
   * @param {string} checkoutSessionId - The ID of the completed checkout session
   * @param {Object} eventData - Additional data about the checkout event
   * @returns {Promise<Object>} The tracking result
   */
  trackCheckoutEvent: async (checkoutSessionId, eventData = {}) => {
    try {
      const response = await api.post('/polar/track-checkout', {
        checkout_session_id: checkoutSessionId,
        event_data: {
          ...eventData,
          timestamp: new Date().toISOString(),
          source: 'web_client'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to track checkout event:', error);
      // Don't throw error, just return failure
      return { success: false, message: error.response?.data?.message || 'Failed to track checkout event' };
    }
  }
};

export default polarService;